import React, { useState, useMemo, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { MentionsInput, Mention } from "react-mentions";
import Linkify from "linkify-react";
import { RenderIf, FileSlider, ClickMore, ImageGrid } from "shared/components";
import { Urls } from "shared/constants/url";
import { SendIcon, Like } from "shared/components/Icons";
import { calculateTimeAgo } from "shared/helper/formatDate";
import { renderLink } from "shared/helper/getYoutubeVideoId";
import Member from "shared/media/user/userIcon.png";
import {
  useDeleteCommentMutation,
  useUpdateCommentMutation,
  useCommunityDiscussionCommentLikeMutation,
  useDeleteCommunityDiscussionCommentLikeMutation,
} from "redux/api/community";
import { useGetAllUsersWithoutFilterQuery } from "redux/api/siteUsers";
import { custom, customStyle } from "shared/helper/mentionStyle";
import "./DiscussionComment.scss";

const { REACT_APP_BASE_PHOTO_URL_MAIN } = process.env;

const CommunityDiscussionComment = ({
  siteUserId,
  description,
  userName,
  userSurname,
  userPhoto,
  createDate,
  newsCommentFiles,
  commentId,
  refetch,
  isLiked,
  likeCount,
  communityDiscussionId,
}) => {
  const { t } = useTranslation();
  const { id } = useSelector((state) => state.user.user);

  const [modal, setModal] = useState(false);
  const [text, setText] = useState(description || "");
  const [files, setFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [selectedId, setSelectedId] = useState([]);
  const [suggestion, setSuggestion] = useState([]);

  const { data, isSuccess: usersSuccess } = useGetAllUsersWithoutFilterQuery();

  const [deleteComment, { isSuccess: deleteSuccess }] =
    useDeleteCommentMutation();
  const [updateComment, { isSuccess: updateSuccess }] =
    useUpdateCommentMutation();
  const [commentLike] = useCommunityDiscussionCommentLikeMutation();
  const [deleteCommentLike] = useDeleteCommunityDiscussionCommentLikeMutation();

  const handleInputChange = (e) => setText(e.target.value);
  const handleTagClick = (id) => setSelectedId((prev) => [...prev, id]);

  const selectedIds = useMemo(() => {
    const ids = [...selectedId];
    const regex = /@(\d+)/g;
    let match;
    while ((match = regex.exec(description)) !== null) {
      const id = parseInt(match[1]);
      if (!ids.includes(id)) ids.push(id);
    }
    return ids;
  }, [description, selectedId]);

  const formattedMessage = useMemo(() => {
    let msg = text;
    selectedId.forEach((id) => {
      msg = msg.replace(new RegExp(`@\\[.*?\\]\\(${id}\\)`, "g"), `@${id}`);
    });
    return msg;
  }, [text, selectedId]);

  const updatedDescription = useMemo(() => {
    return selectedIds.reduce((desc, id) => {
      const user = data?.data?.find((u) => u.id === id);
      return desc.replace(
        new RegExp(`@${id}`, "g"),
        user ? `${user.firstName} ${user.lastName}` : ""
      );
    }, description);
  }, [selectedIds, description, data]);

  const titles = [
    { id: uuid(), title: t("edit"), onClick: () => setModal(true) },
    {
      id: uuid(),
      title: t("delete"),
      onClick: (e) => {
        e.preventDefault();
        deleteComment(commentId);
      },
    },
  ];

  const newFiles = useMemo(
    () =>
      newsCommentFiles?.map((f) => ({
        ...f,
        fileUrl: `${REACT_APP_BASE_PHOTO_URL_MAIN}${f.fileUrl}`,
      })) || [],
    [newsCommentFiles]
  );

  const updatePostComment = () => {
    const formData = new FormData();
    formData.append("communityDiscussionId", communityDiscussionId);
    formData.append("communityDiscussionCommentId", commentId);
    if (formattedMessage) formData.append("description", formattedMessage);
    deletedFiles.forEach((id) => formData.append("DeletedFiles", id));
    files.forEach((f) => !f?.id && formData.append("Files", f));
    updateComment(formData);
  };

  const likeCommentHandler = () => {
    const formData = new FormData();
    formData.append("CommunityDiscussionCommentId", commentId);
    commentLike(formData);
  };

  const removeLikeComment = () =>
    deleteCommentLike({ communityDiscussionCommentId: commentId });

  useEffect(() => {
    if (usersSuccess) {
      setSuggestion(
        data?.data?.map((u) => ({
          id: u.id,
          display: `${u.firstName} ${u.lastName}`,
        })) || []
      );
      setText(updatedDescription);
    }
  }, [usersSuccess, updatedDescription, data]);

  useEffect(() => {
    if (deleteSuccess || updateSuccess) {
      refetch?.();
      setModal(false);
      setFiles([]);
      setDeletedFiles([]);
    }
  }, [deleteSuccess, updateSuccess]);

  return (
    <div className="cardMainCommentList">
      <div className="cardMainCommentListBox">
        <div className="cardMainCommentListBoxLeft">
          <Link to={Urls.POSTS_URL + `/${siteUserId}`}>
            <img
              src={
                userPhoto
                  ? `${REACT_APP_BASE_PHOTO_URL_MAIN}${userPhoto}`
                  : Member
              }
              alt="user"
            />
          </Link>
        </div>
        <div className="cardMainCommentListBoxRight">
          <RenderIf condition={!modal}>
            <div className="cardMainCommentListBoxRightInner noFile">
              <div className="d-flex justify-content-between align-items-center">
                <Link to={Urls.POSTS_URL + `/${siteUserId}`}>
                  <span className="name">
                    {userName} {userSurname}
                  </span>
                </Link>
                <div className="d-flex align-items-center">
                  <span className="time mx-2">
                    {calculateTimeAgo(createDate)}
                  </span>
                  <RenderIf condition={id === siteUserId}>
                    <ClickMore buttonTitles={titles} />
                  </RenderIf>
                </div>
              </div>

              <RenderIf condition={text}>
                <div className={newsCommentFiles?.length ? "mb-3" : ""}>
                  <Linkify options={{ render: renderLink }}>
                    {updatedDescription}
                  </Linkify>
                </div>
              </RenderIf>

              <RenderIf condition={newsCommentFiles?.length}>
                <ImageGrid forComment>
                  {newsCommentFiles?.map((item) =>
                    item.fileUrl.includes(".mp4") ? (
                      <video
                        key={uuid()}
                        src={`${REACT_APP_BASE_PHOTO_URL_MAIN}${item.fileUrl}`}
                        controls
                      />
                    ) : (
                      <img
                        key={uuid()}
                        src={`${REACT_APP_BASE_PHOTO_URL_MAIN}${item.fileUrl}`}
                        alt="comment"
                      />
                    )
                  )}
                </ImageGrid>
              </RenderIf>
            </div>
          </RenderIf>

          <RenderIf condition={modal}>
            <div className="cardMainCommentListBoxRightInner">
              <Link to={Urls.POSTS_URL + `/${siteUserId}`}>
                <span className="name">
                  {userName} {userSurname}
                </span>
              </Link>

              <MentionsInput
                value={text}
                onChange={handleInputChange}
                placeholder={t("writeComment")}
                style={customStyle}>
                {suggestion?.map((item) => (
                  <Mention
                    key={uuid()}
                    trigger="@"
                    data={[item]}
                    onAdd={handleTagClick}
                    style={{ ...custom }}
                  />
                ))}
              </MentionsInput>

              <div className="d-flex align-items-center justify-content-between mt-2">
                <button
                  onClick={() => {
                    setModal(false);
                    setText(description);
                  }}>
                  {t("cancel")}
                </button>
                <SendIcon
                  onClick={updatePostComment}
                  width="20px"
                  height="20px"
                  className="mx-2 sendButton"
                />
              </div>
            </div>
            <FileSlider
              forComment
              file={newFiles}
              multiple
              handleImageID={setDeletedFiles}
              handleImageFile={setFiles}
            />
          </RenderIf>

          <div
            onClick={isLiked ? removeLikeComment : likeCommentHandler}
            className={`${isLiked ? "like-active" : ""} newsCardTwoIcon mt-1`}>
            <Like />{" "}
            <span>
              {t("like")} ({likeCount})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityDiscussionComment;
